'use client';

import clsx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Loader } from 'ui';

import { useCurrentLocale } from '@/shared/utils/client';

import styles from './Image.module.scss';
import { ImagesProps } from './types';
import { rewriteFinesioUrl } from './utils';

const errorURL = '/ui/a163521f-619f-4c7c-b26e-5485dc482a00.jpg';

export const Image = ({
  breakpoints,
  srcSet,
  sizes,
  loading = 'lazy',
  loader = true,
  containerClassName = '',
  forcePortraitSrcSet = false,
  base64 = false,
  ...rest
}: ImagesProps) => {
  const locale = useCurrentLocale();

  let imageSrc = rewriteFinesioUrl(rest.src, locale);
  let srcAttr = imageSrc;

  const imageRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(loader);

  const handleError = () => {
    if (!imageRef.current) {
      return;
    }

    imageRef.current.onerror = null;
    imageRef.current.src = `${rewriteFinesioUrl(errorURL, locale)}?class=public`;
    imageRef.current.srcset = renderSrcSet(true) ?? '';
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  const renderSizes = () => {
    if (!breakpoints || !imageSrc) {
      return undefined;
    }

    const sizesArray: string[] = [];

    Object.keys(breakpoints)
      .reverse()
      .forEach((key, index) => {
        if (Array.isArray(breakpoints[Number(key)])) {
          const widthArray = breakpoints[Number(key)] as number[];

          sizesArray.push(`(min-width:${key}px) ${widthArray[0]}px`);

          return;
        }

        sizesArray.push(`(min-width:${key}px) ${breakpoints[Number(key)]}px`);
      });

    const sizes = sizesArray.join(', ');

    return sizes;
  };

  const renderSrcSet = (error = false) => {
    if (!breakpoints || !imageSrc) {
      return undefined;
    }

    const srcSetArray: string[] = [];
    const sizes: number[] = [];
    const baseUrl = error ? rewriteFinesioUrl(errorURL, locale) : imageSrc;

    Object.keys(breakpoints).forEach((key) => {
      if (Array.isArray(breakpoints[Number(key)])) {
        (breakpoints[Number(key)] as number[]).map((number) => {
          if (sizes.includes(number)) {
            return;
          }

          sizes.push(number);
        });

        return;
      }

      if (sizes.includes(Number(breakpoints[Number(key)]))) {
        return;
      }

      sizes.push(Number(breakpoints[Number(key)]));
    });

    sizes.sort();

    sizes.forEach((size) => {
      srcSetArray.push(`${baseUrl}?class=x${size} ${size}w`);
    });

    const srcSet = srcSetArray.join(', ');

    return srcSet;
  };

  useEffect(() => {
    if (!imageRef.current || !imageRef.current.complete) {
      return;
    }

    const errorLoadingImgBeforeHydration =
      imageRef.current.complete && imageRef.current.naturalHeight === 0;

    if (errorLoadingImgBeforeHydration) {
      handleError();
    }

    handleLoad();
  }, []);

  if ((srcSet || sizes || breakpoints) && !/^.*\/x\d+$/.test(imageSrc || '')) {
    srcAttr = `${imageSrc}?class=public`;
  }

  if (typeof window === 'undefined') {
    // eslint-disable-next-line jsx-a11y/alt-text
    return (
      <div
        className={clsx(
          styles.image__container,
          'imageContainer',
          containerClassName
        )}
      >
        <img
          {...rest}
          loading={base64 ? 'eager' : loading}
          sizes={base64 ? undefined : (sizes ?? renderSizes())}
          srcSet={base64 ? undefined : (srcSet ?? renderSrcSet())}
          src={base64 ? `data:image/png;base64, ${rest.src}` : srcAttr}
        />
        {isLoading && <Loader backgroundColor="#fff" />}
      </div>
    );
  }

  // eslint-disable-next-line jsx-a11y/alt-text
  return (
    <div
      className={clsx(
        styles.image__container,
        'imageContainer',
        containerClassName
      )}
    >
      <img
        ref={imageRef}
        {...rest}
        loading={base64 ? 'eager' : loading}
        sizes={base64 ? undefined : (sizes ?? renderSizes())}
        srcSet={base64 ? undefined : (srcSet ?? renderSrcSet())}
        onLoad={base64 ? undefined : handleLoad}
        onError={base64 ? undefined : handleError}
        src={base64 ? `data:image/png;base64, ${rest.src}` : srcAttr}
      />
      {isLoading && <Loader backgroundColor="#fff" />}
    </div>
  );
};
